import * as React from "react";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import repository from "client/repository";
import {AzureRegionResource} from "client/resources/azureRegionResource";
import {RegionPublicIpPrefixResource} from "client/resources/regionPublicIpPrefixResource";
import {AdministratorRoleCallout} from "shared/AdministratorRoleCallout";
import {RegionPublicIpPrefixChange} from "./regionPublicIpPrefixChanges";

export const changes: Record<RegionPublicIpPrefixChange, {label: string; title: string; action: string; consequence: string}> = {
    Publish: {
        label: "Publish",
        title: "Publish IP Prefix",
        action: "publish",
        consequence: "Customers won't see this IP prefix in Control Center until the region's IP ranges are republished. Give them time to update their firewalls before making it available.",
    },
    Unpublish: {
        label: "Unpublish",
        title: "Unpublish IP Prefix",
        action: "unpublish",
        consequence: "Customers will stop seeing this IP prefix in Control Center once the region's IP ranges are republished. It can only be unpublished once no IP addresses are allocated from it.",
    },
    MakeAvailable: {
        label: "Make available",
        title: "Make IP Prefix Available",
        action: "make available",
        consequence: "Clusters created or upgraded in this region may be allocated IP addresses from this prefix. Only do this once customers have had enough notice to update their firewalls.",
    },
    MakeUnavailable: {
        label: "Make unavailable",
        title: "Make IP Prefix Unavailable",
        action: "make unavailable",
        consequence: "Clusters created or upgraded in this region won't be allocated IP addresses from this prefix. Existing clusters keep their IP addresses until they're recreated. The region's only available Standard IP prefix can't be made unavailable.",
    },
};

interface Props {
    azureRegion: AzureRegionResource;
    prefix: RegionPublicIpPrefixResource;
    change: RegionPublicIpPrefixChange;
    onChange: (prefix: RegionPublicIpPrefixResource) => void;
}

type State = DataBaseComponentState;

class ChangeRegionPublicIpPrefixDialog extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    render() {
        const change = changes[this.props.change];
        return <SaveDialogLayout title={change.title}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonLabel="Yes"
                                 cancelButtonLabel="No"
                                 onSaveClick={this.save}>
            <AdministratorRoleCallout action={`${change.action} an IP prefix`}/>
            <p>Are you sure you want to {change.action} the <b>{this.props.prefix.Name}</b> ({this.props.prefix.IpPrefix}) IP prefix?</p>
            <p>{change.consequence}</p>
        </SaveDialogLayout>;
    }

    private save = (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            const {azureRegion, prefix} = this.props;
            switch (this.props.change) {
                case "Publish":
                case "Unpublish": {
                    const isPublished = this.props.change === "Publish";
                    await repository.AzureRegions.changePublicIpPrefixPublication(azureRegion.Id, prefix.Name, {IsPublished: isPublished});
                    this.props.onChange({...prefix, IsPublished: isPublished});
                    break;
                }
                case "MakeAvailable":
                case "MakeUnavailable": {
                    const isAvailable = this.props.change === "MakeAvailable";
                    await repository.AzureRegions.changePublicIpPrefixAvailability(azureRegion.Id, prefix.Name, {IsAvailable: isAvailable});
                    this.props.onChange({...prefix, IsAvailable: isAvailable});
                    break;
                }
            }
        });
    }
}

export default ChangeRegionPublicIpPrefixDialog;
